import { Component, OnInit, inject } from '@angular/core';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { AuthService } from '@auth/auth.service';
import { I18nService } from '@shared/services/i18n.service';
import { CookieConsentService } from '@services/cookie-consent.service';
import { ToastComponent } from '@shared/components/toast/toast.component';
import { HeaderComponent } from '@app/layout/header/header.component';
import { FooterComponent } from '@app/layout/footer/footer.component';

@Component({
	selector: 'app-root',
	imports: [RouterOutlet, CommonModule, TranslateModule, ToastComponent, HeaderComponent, FooterComponent],
	standalone: true,
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
	authService = inject(AuthService);
	i18nService = inject(I18nService);
	private readonly cookieConsentService = inject(CookieConsentService);

	// Make translateService public so it can be accessed from the template
	public translateService = inject(TranslateService);
	private readonly router = inject(Router);

	currentUrl = '';
	fullWidth = false;
	errorPages = false;

	constructor() {
		// Initialize translations as early as possible
		this.initializeTranslations();
	}

	ngOnInit(): void {
		// Initialize cookie consent listener (CCM19)
		this.cookieConsentService.initialize();

		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
			this.currentUrl = event.urlAfterRedirects;
			this.fullWidth = this.currentUrl.startsWith('/dashboard');
			this.errorPages = this.currentUrl.startsWith('/404') || this.currentUrl.startsWith('/500');
		});

		// Add global method for testing silent refresh
		(window as unknown as { testSilentRefresh: () => void }).testSilentRefresh = () => {
			// console.log('🔄 Testing: Manually triggering silent refresh...');
			// this.authService.triggerSilentRefresh();
		};
	}

	/**
	 * Initialize translations for the application
	 */
	private initializeTranslations(): void {
		this.translateService.addLangs(['en', 'de']);
		this.translateService.setDefaultLang('en');

		// Try to use saved language or browser language
		const savedLang = localStorage.getItem('selectedLanguage');
		if (savedLang && ['en', 'de'].includes(savedLang)) {
			this.translateService.use(savedLang);
		} else {
			const browserLang = this.translateService.getBrowserLang();
			if (browserLang && ['en', 'de'].includes(browserLang)) {
				this.translateService.use(browserLang);
			} else {
				this.translateService.use('en');
			}
		}
	}
}
